import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import type { RowSelectionState, Updater } from '@tanstack/react-table';
import { ReactComponent as ReportIcon } from '../../../../assets/icons/report.svg';
import { TableV1 } from '../../../../components/Table/TableV1';
import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTimesheetRouteId } from '../../context/TimesheetRouteIdContext';
import { FormValidationMessage } from '../../../../components/FormValidationMessage/FormValidationMessage';
import { TableSkeletonLoader } from '../../../../components/SkeletonLoader/Table.SkeletonLoader';
import { useSingleTimesheet } from '../../../../queries/hooks/useSingleTimesheet';
import { Timesheet, TimesheetRow, TimesheetStatus } from '../../../../types';
import { isSubmitter } from '../../../../utils/roles';
import { formatWeekName } from '../../../../utils/formatters';
import { useSingleTableColumns } from '../../hooks/useSingleTableColumns';
import { SingleTimesheetHeader } from './SingleTimesheetHeader';
import {
  SINGLE_TS_TABLE_MOCK_DATA,
  SINGLE_TS_TABLE_MOCK_DATA_SUBMITTED,
} from '../../../../components/Onboarding/OnboardingMockData';
import OnboardingSingleTimesheet from './OnboardingSingleTimesheet';
import { userState } from '../../../../state/UIState';
import RejectedTimesheetBanner from './RejectedTimesheetBanner';
import { getWeeklyTimesheetWeekStart } from '../../../../utils/getWeeklyTimesheetWeekStart';
import { OverideHolidayModal } from './OverideHolidayModal';
import { overideDataAtom } from '../../../../state/UITimesheetsState';
import { SHOW_STRATEGIC_INITIATIVES_TO } from '../../../../queries/constants';
import { usePendingMonthView } from '../../../IssuedTimesheets/PendingMonthViewContext';
import { MonthViewSelectionToolbar } from './MonthViewSelectionToolbar';
import { CopyRowsToWeekMenu } from './CopyRowsToWeekMenu';
import { CopyRowsHolidayBanner } from './CopyRowsHolidayBanner';
import { selectedRowsSkipSourceHolidayColumns } from '../../../../utils/copyRowsBetweenTimesheets';
import { PENDING_MONTH_WEEK_READY_ATTR } from '../../../../utils/scrollToPendingMonthTimesheetAnchor';

type SingleTimesheetTableProps = {
  /** Pending timesheets month page: stack full weekly editors without duplicating chrome. */
  embeddedInMonthView?: boolean;
  /** Submitted week on month page: read-only cells, row selection + copy still enabled. */
  readOnlySubmitted?: boolean;
  /** Month list row: stabilizes header / withdraw while `/timesheet/:id` refetches. */
  monthListTimesheet?: Timesheet;
  /** Month stacked view: URL hash targets this week — accent stripe on table card only (not week title). */
  embeddedWeekAccentStripe?: boolean;
};

export function SingleTimesheetTable({
  embeddedInMonthView = false,
  readOnlySubmitted = false,
  monthListTimesheet,
  embeddedWeekAccentStripe = false,
}: SingleTimesheetTableProps) {
  const { colors } = useTheme();
  const id = useTimesheetRouteId();
  const user = useAtomValue(userState);
  // Manager-Submitters (OP Manager + Operating Partner) act as submitters on their own OP pages too.
  const isOP = isSubmitter(user);
  const [overideData, setOverideData] = useAtom(overideDataAtom);
  const monthView = usePendingMonthView();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [copyMenuAnchor, setCopyMenuAnchor] = useState<null | HTMLElement>(null);

  const isOnboarding = id === 'demo';
  const demoWeekStartIso = useMemo(() => {
    if (!isOnboarding) return '';
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    return date.toISOString();
  }, [isOnboarding]);

  const { data: singleTimesheet, isLoading: dataIsLoading } = useSingleTimesheet({
    id: Number(id),
    enabled: !isOnboarding,
  });

  const weekRef = useMemo(() => {
    if (embeddedInMonthView && readOnlySubmitted && monthListTimesheet) {
      return singleTimesheet ?? monthListTimesheet;
    }
    return singleTimesheet ?? undefined;
  }, [embeddedInMonthView, readOnlySubmitted, monthListTimesheet, singleTimesheet]);

  const columns = useSingleTableColumns(
    weekRef?.weekStart ?? demoWeekStartIso,
    weekRef?.weekDates ?? [],
    {
      enableRowSelection: embeddedInMonthView,
      readOnly: readOnlySubmitted,
    }
  );

  const [data, setData] = useState<TimesheetRow[]>([]);
  const [updateOnboardingData, setUpdateOnboardingData] = useState(false);

  const weekStart = useMemo(() => {
    if (!weekRef) return '';
    return getWeeklyTimesheetWeekStart(weekRef);
  }, [weekRef]);

  const weekLength = useMemo(() => {
    if (!weekRef) return 0;
    return weekRef.weekDates.length + weekRef.holidays.length;
  }, [weekRef]);

  const weekName = formatWeekName(weekStart, weekLength);

  useEffect(() => {
    setRowSelection({});
  }, [id]);

  useEffect(() => {
    const updatedTimesheetRows = (weekRef?.timesheetRows || [])
      .map((row) => {
        return {
          ...row,
          editDisabled:
            weekRef?.status !== TimesheetStatus.ISSUED &&
            weekRef?.status !== TimesheetStatus.REJECTED,
        };
      })
      .filter((row) => {
        if (row.name === 'Strategic Initiatives' && !row.dealId) {
          return SHOW_STRATEGIC_INITIATIVES_TO.some(
            (u) => u.id === user?.id && u.name === user?.name
          );
        }
        return true;
      });
    setData(updatedTimesheetRows || []);
  }, [weekRef, user]);

  useEffect(() => {
    if (isOnboarding && !updateOnboardingData) {
      setData(SINGLE_TS_TABLE_MOCK_DATA);
    }
  }, [isOnboarding, updateOnboardingData]);

  useEffect(() => {
    if (updateOnboardingData) {
      setData(SINGLE_TS_TABLE_MOCK_DATA_SUBMITTED);
    }
  }, [updateOnboardingData]);

  const onUpdateOnboardingData = () => {
    setUpdateOnboardingData(true);
  };

  const handleRowSelectionChange = useCallback((updater: Updater<RowSelectionState>) => {
    setRowSelection((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const table = useReactTable({
    data: data ?? [],
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (item) => String(item.id),
    ...(embeddedInMonthView
      ? {
          state: { rowSelection },
          onRowSelectionChange: handleRowSelectionChange,
          enableRowSelection: true,
          enableMultiRowSelection: true,
        }
      : {}),
    meta: {
      updateData: (rowIndex: number, columnId: string, value: string) => {
        setData((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            }
            return row;
          })
        );
      },
    },
  });

  const selectedRows = useMemo(
    () => table.getFilteredRowModel().rows.filter((r) => r.getIsSelected()).map((r) => r.original),
    // rowSelection drives selection; table reference updates with data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, rowSelection]
  );

  const selectedCount = selectedRows.length;

  const showSourceHolidayBanner = useMemo(() => {
    if (!embeddedInMonthView || !weekRef || selectedCount === 0) return false;
    return selectedRowsSkipSourceHolidayColumns(
      selectedRows,
      weekStart,
      weekRef.weekDates ?? []
    );
  }, [embeddedInMonthView, weekRef, selectedCount, selectedRows, weekStart]);

  const validationErrors = useMemo(() => {
    const accessors = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const errors = {};

    accessors.forEach((accessor) => {
      const total = table
        .getFilteredRowModel()
        .rows.reduce((total, row) => total + Number(row.getValue(accessor)), 0);
      const formatedAccessorName = accessor[0].toUpperCase() + accessor.slice(1);
      if (total > 100) {
        errors[
          accessor
        ] = `${formatedAccessorName}, ${weekName} timesheet exceeds 100%. Please correct the data.`;
      }
    });
    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, data]);

  const blockOnTimesheetLoading =
    !isOnboarding &&
    dataIsLoading &&
    !(embeddedInMonthView && readOnlySubmitted && monthListTimesheet);

  if (blockOnTimesheetLoading) return <TableSkeletonLoader />;

  const tableWrapperStyle = embeddedInMonthView
    ? {
        maxHeight: undefined as number | undefined,
        overflowX: 'scroll' as const,
        border: 'none',
        borderRadius: 0,
        overflowY: 'visible' as const,
      }
    : {
        maxHeight: 'calc(100vh - 288px)',
        overflowX: 'scroll' as const,
        border: `1px solid ${colors.border.default}`,
        overflowY: 'scroll' as const,
      };

  const timesheetTable = (
    <TableV1
      className='single-timesheet'
      hasFooter
      table={table}
      height='auto'
      disableRowHover
      wrapperStyle={tableWrapperStyle}
      selectionCheckboxOnly={embeddedInMonthView}
      highlightMultiSelectedRows={embeddedInMonthView}
    />
  );

  const validationBlock = (
    <Box mt={2} display={'flex'} flexDirection={'column'} gap={2}>
      {Object.keys(validationErrors).map((key) => {
        return (
          <FormValidationMessage
            key={key}
            text={validationErrors[key]}
            Icon={<ReportIcon />}
            textColor={colors.textStatus.error}
          />
        );
      })}
    </Box>
  );

  const isRejected = weekRef?.status === TimesheetStatus.REJECTED;

  if (embeddedInMonthView) {
    return (
      <>
        <Box sx={{ mb: 0.25 }}>
          <SingleTimesheetHeader
            data={data}
            table={table}
            updateOnboardingData={updateOnboardingData}
            embeddedInMonthView
            readOnlySubmitted={readOnlySubmitted}
            monthListTimesheet={monthListTimesheet}
          />
        </Box>
        <Box
          {...{ [PENDING_MONTH_WEEK_READY_ATTR]: 'true' }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            mb: 3,
            border: `1px solid ${colors.border.default}`,
            borderRadius: '4px',
            overflow: 'hidden',
            bgcolor: embeddedWeekAccentStripe
              ? alpha(colors.textAccent.default, 0.06)
              : undefined,
          }}
        >
          {embeddedWeekAccentStripe ? (
            <Box
              aria-hidden
              sx={{
                width: 4,
                flexShrink: 0,
                bgcolor: colors.textAccent.default,
              }}
            />
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {selectedCount > 0 && (
              <MonthViewSelectionToolbar
                selectedCount={selectedCount}
                onClearSelection={() => {
                  setRowSelection({});
                  setCopyMenuAnchor(null);
                }}
                onOpenCopyMenu={(el) => setCopyMenuAnchor(el)}
              />
            )}
            {showSourceHolidayBanner && <CopyRowsHolidayBanner />}
            {isRejected && weekRef && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${colors.border.default}`,
                }}
              >
                <RejectedTimesheetBanner timesheet={weekRef} embeddedInMonthView />
              </Box>
            )}
            {timesheetTable}
          </Box>
        </Box>
        {weekRef && monthView?.allTimesheets && (
          <CopyRowsToWeekMenu
            open={Boolean(copyMenuAnchor)}
            anchorEl={copyMenuAnchor}
            onClose={() => setCopyMenuAnchor(null)}
            onCopied={() => setRowSelection({})}
            allTimesheets={monthView.allTimesheets}
            sourceTimesheet={weekRef}
            selectedRows={selectedRows}
            userId={user?.id}
          />
        )}
        {validationBlock}
        {isOnboarding && (
          <OnboardingSingleTimesheet onUpdateOnboardingData={onUpdateOnboardingData} />
        )}
        <OverideHolidayModal
          open={!!overideData}
          onClose={() => {
            setOverideData(null);
          }}
        />
      </>
    );
  }

  return (
    <>
      <SingleTimesheetHeader
        data={data}
        table={table}
        updateOnboardingData={updateOnboardingData}
        embeddedInMonthView={false}
      />
      {isRejected && weekRef && (
        <RejectedTimesheetBanner timesheet={weekRef} embeddedInMonthView={false} />
      )}
      {timesheetTable}
      {validationBlock}
      {isOnboarding && (
        <OnboardingSingleTimesheet onUpdateOnboardingData={onUpdateOnboardingData} />
      )}
      <OverideHolidayModal
        open={!!overideData}
        onClose={() => {
          setOverideData(null);
        }}
      />
    </>
  );
}
